import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, LogOut, FileText, Calendar, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClienteDashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('userData');
    
    if (!token) {
      navigate('/cliente/login');
      return;
    }

    if (user) {
      setUserData(JSON.parse(user));
    }

    carregarProcessos();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const carregarProcessos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cliente/processos`, getAuthHeader());
      setProcessos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar processos');
      console.error(error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/cliente/login');
  };

  const getStatusBadge = (status) => {
    const config = {
      'em andamento': { color: 'bg-blue-500', icon: Clock },
      'concluído': { color: 'bg-green-500', icon: CheckCircle },
      'aguardando': { color: 'bg-yellow-500', icon: AlertCircle }
    };
    return config[status] || { color: 'bg-gray-500', icon: FileText };
  };

  const processosEmAndamento = processos.filter(p => p.status === 'em andamento').length;
  const processosConcluidos = processos.filter(p => p.status === 'concluído').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Scale className="w-8 h-8 text-gold" />
              <div>
                <h1 className="font-serif text-xl font-bold text-navy">
                  Área do Cliente
                </h1>
                <p className="text-sm text-gray-600">
                  Bem-vindo, {userData?.nome}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="border-gold text-navy hover:bg-gold hover:text-white"
              >
                Ver Site
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Message */}
        <Alert className="mb-8 border-gold bg-gold/10">
          <Scale className="h-5 w-5 text-gold" />
          <AlertDescription className="text-navy font-medium ml-2">
            Acompanhe aqui todos os seus processos trabalhistas. 
            Em caso de dúvidas, entre em contato através do WhatsApp: (62) 9 9695-1404
          </AlertDescription>
        </Alert>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="card-elegant">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">Total de Processos</p>
                  <p className="text-3xl font-bold text-navy">{processos.length}</p>
                </div>
                <FileText className="w-12 h-12 text-gold" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">Em Andamento</p>
                  <p className="text-3xl font-bold text-blue-600">{processosEmAndamento}</p>
                </div>
                <Clock className="w-12 h-12 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">Concluídos</p>
                  <p className="text-3xl font-bold text-green-600">{processosConcluidos}</p>
                </div>
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Processos List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-navy flex items-center gap-2">
              <FileText className="w-6 h-6" />
              Meus Processos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-gold mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">Carregando processos...</p>
              </div>
            ) : processos.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg mb-2">Nenhum processo cadastrado ainda</p>
                <p className="text-gray-500 text-sm">
                  Quando houver novos processos, eles aparecerão aqui
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {processos.map((processo) => {
                  const statusConfig = getStatusBadge(processo.status);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <Card key={processo.id} className="border-l-4 border-gold hover:shadow-lg transition" data-testid={`processo-${processo.id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-start gap-3 mb-3">
                              <FileText className="w-6 h-6 text-navy mt-1 flex-shrink-0" />
                              <div className="flex-1">
                                <h3 className="font-serif text-xl font-bold text-navy mb-2">
                                  {processo.numero_processo}
                                </h3>
                                <Badge className="bg-navy text-white mb-3">
                                  {processo.tipo}
                                </Badge>
                              </div>
                            </div>
                            
                            <div className="ml-9">
                              <p className="text-gray-700 mb-4 leading-relaxed">
                                {processo.descricao}
                              </p>

                              {processo.valor_causa && (
                                <div className="bg-gold/10 border border-gold rounded-lg p-3 mb-4">
                                  <p className="text-sm font-semibold text-navy">
                                    Valor da causa: 
                                    <span className="text-gold ml-2 text-lg">
                                      R$ {processo.valor_causa.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                    </span>
                                  </p>
                                </div>
                              )}

                              {processo.historico && processo.historico.length > 0 && (
                                <div className="mt-4 border-t pt-4">
                                  <h4 className="font-semibold text-navy mb-3 flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Histórico de Atualizações
                                  </h4>
                                  <div className="space-y-2">
                                    {processo.historico.map((item, index) => (
                                      <div key={index} className="bg-gray-50 p-3 rounded border-l-2 border-gold">
                                        <p className="text-sm text-gray-700">{item.descricao}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {new Date(item.data).toLocaleString('pt-BR')}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-4">
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  <span>Início: {new Date(processo.data_inicio).toLocaleDateString('pt-BR')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  <span>Última atualização: {new Date(processo.ultima_atualizacao).toLocaleDateString('pt-BR')}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex md:flex-col items-center md:items-end gap-2">
                            <Badge className={`${statusConfig.color} text-white flex items-center gap-2 px-4 py-2`}>
                              <StatusIcon className="w-4 h-4" />
                              {processo.status}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="mt-8 bg-navy text-white">
          <CardContent className="p-6">
            <div className="text-center">
              <Scale className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-serif text-2xl font-bold mb-2">
                Precisa de ajuda?
              </h3>
              <p className="text-gray-200 mb-4">
                Entre em contato conosco para esclarecer dúvidas sobre seus processos
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => window.open('https://wa.me/5562996951404', '_blank')}
                  className="btn-gold"
                >
                  WhatsApp: (62) 9 9695-1404
                </Button>
                <Button 
                  onClick={() => window.location.href = 'mailto:leandro@leandroribeiro.adv.br'}
                  variant="outline"
                  className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-navy"
                >
                  Email: leandro@leandroribeiro.adv.br
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClienteDashboard;
